import { ConnectedPosition, Overlay, OverlayConfig, OverlayRef } from "@angular/cdk/overlay";
import { ComponentPortal, TemplatePortal } from "@angular/cdk/portal";
import {
    ComponentRef,
    computed,
    Directive,
    effect,
    ElementRef,
    HostListener,
    inject,
    input,
    TemplateRef,
    Type,
    ViewContainerRef,
} from "@angular/core";
import { InputFields } from "@lib/base/modules/table/table.types";
import { NavigationSkipped, NavigationStart, Router } from "@angular/router";
import { filter } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

export type ConstructorType<T> = new (...args: any[]) => T;

export interface CellRenderWithInputs<Component extends InstanceType<Type<any>> = InstanceType<Type<any>>> {
    component: ConstructorType<Component>;
    inputData: InputFields<Component>;
};

@Directive({
    selector: "[appFixedOverlay]",
})
export class FixedOverlayDirective {
    overlayContent = input.required<TemplateRef<any> | CellRenderWithInputs>();
    overlayPosition = input<ConnectedPosition>({
        originX: "start",
        originY: "bottom",
        overlayX: "start",
        overlayY: "top",
    });

    overlayMode = input<"click" | "hover">("click");
    hasBackdrop = input<boolean>(false);
    closeOnNavigation = input<boolean>(true);

    routerService = inject(Router);

    overlay = inject(Overlay);
    elementRef = inject(ElementRef);
    viewContainerRef = inject(ViewContainerRef);

    overlayRef?: OverlayRef | null;
    componentRef?: ComponentRef<any> | null;

    closeSubscription = this.routerService.events.pipe(
        filter(_ => this.closeOnNavigation()),
        filter(event => event instanceof NavigationStart || event instanceof NavigationSkipped),
        takeUntilDestroyed(),
    ).subscribe((_) => {
        this.closeOverlay();
    });

    componentInputs = computed(() => {
        const overlayContent = this.overlayContent();
        if (!(overlayContent instanceof TemplateRef)) {
            return overlayContent.inputData;
        }
        return null;
    });

    inputsToComponentEffects = effect(() => {
        const componentInputs = this.componentInputs();
        if (componentInputs) {
            Object.entries(componentInputs).forEach(([key, value]) => {
                this.componentRef?.setInput(key, value);
            });
        }
    });

    @HostListener("click")
    toggleOverlay() {
        if (this.overlayMode() !== "click") return;
        if (this.overlayRef == null) {
            this.openOverlay();
        } else {
            this.closeOverlay();
        }
    }

    @HostListener("mouseenter")
    openOnHover() {
        if (this.overlayMode() !== "hover") return;
        console.log("enter overlay");
        this.openOverlay();
    }

    @HostListener("mouseleave")
    closeOnHover() {
        if (this.overlayMode() !== "hover") return;
        console.log("leave overlay");
        this.closeOverlay();
    }

    openOverlay() {
        const positionStrategy = this.overlay.position()
            .flexibleConnectedTo(this.elementRef)
            .withPositions([this.overlayPosition()]);

        const overlayConfig = new OverlayConfig({
            hasBackdrop: this.hasBackdrop(),
            backdropClass: "transparent-backdrop",
            positionStrategy,
            disposeOnNavigation: true,
        });

        this.overlayRef = this.overlay.create(overlayConfig);
        this.overlayRef.addPanelClass("example-overlay");
        this.overlayRef.backdropClick().subscribe(() => {
            this.closeOverlay();
        });

        const content = this.overlayContent();
        if (content instanceof TemplateRef) {
            const templatePortal = new TemplatePortal(content, this.viewContainerRef);
            this.overlayRef.attach(templatePortal);
        } else {
            const componentPortal = new ComponentPortal(content.component);
            this.componentRef = this.overlayRef.attach(componentPortal);
            Object.entries(content.inputData).forEach(([key, value]) => {
                this.componentRef?.setInput(key, value);
            });
        }
    }

    closeOverlay() {
        if (this.overlayRef) {
            this.overlayRef.dispose();
            this.overlayRef = null;
            this.componentRef = null;
        }
    }
}
